import GoliGummiesCard from "../components/GoliGummiesCard";
import SiteNav from "../components/SiteNav";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Heart } from "lucide-react";
import Brand, { GoliMark } from "../components/Brand";

/* /goli — public dedicated sales page for Mike's Goli wellness partner.
   Anyone can visit this page (no login required). All CTAs inside the
   GoliGummiesCard component link directly to Mike's go.goli.com sales URL
   and partners.goli.com referral signup URL. */

const GOLI_CUSTOMER = "https://customer-assets.emergentagent.com/job_grace-community-plus/artifacts/gs6eb9by_Screenshot%202026-05-13%20221254.png";
const G = "#2E9F3A";
const O = "#F39A1F";
const WINE = "#0F172A";
const TEAL = "#287F8E";

export default function GoliPage() {
  return (
    <div className="min-h-screen" style={{ background: "#FFFFFF" }}>
      <SiteNav />

      {/* PAGE HERO — bright cream + green, NO DARK BACKGROUNDS */}
      <section className="relative overflow-hidden"
               style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 50%, #E8F5DD 100%)" }}>
        <div className="relative max-w-6xl mx-auto px-6 md:px-8 py-12 md:py-16">
          <Link to="/" className="text-[11px] tracking-[0.3em] uppercase font-bold hover:opacity-70 transition"
                style={{ color: G }} data-testid="goli-page-back">
            ← <Brand />
          </Link>

          <div className="grid md:grid-cols-[1fr_420px] gap-8 md:gap-10 items-center mt-5">
            {/* Left — copy */}
            <div>
              <div className="flex items-center gap-3 flex-wrap mb-4">
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
                  ★ <Brand /> · WELLNESS PARTNER ★
                </div>
                <span className="text-[10px] tracking-[0.18em] uppercase font-bold px-2.5 py-1 rounded-full text-white"
                      style={{ background: G }}>FREE SHIPPING</span>
              </div>

              <div className="inline-block bg-white rounded-2xl px-6 py-4 shadow-md mb-5"
                   style={{ border: `3px solid ${G}` }}>
                <GoliMark size="xl" />
              </div>

              <h1 className="text-5xl md:text-6xl uppercase leading-[0.95]"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: WINE, textWrap: "balance" }}>
                Easy-To-Swallow<br/>Gummy Wellness<span style={{ color: O }}>.</span>
              </h1>

              <p className="text-lg md:text-xl italic mt-5 max-w-xl" style={{ color: "#0F172A" }}>
                Clean, plant-based gummy supplements — Mike&apos;s pick. <strong className="not-italic" style={{ color: WINE }}>No more horse-pills.</strong> Just real wellness — and real commission for anyone who shares it.
              </p>

              <div className="mt-6 flex items-center gap-2.5 text-sm" style={{ color: WINE }}>
                <ShieldCheck className="w-5 h-5" style={{ color: G }} />
                <span><strong>Trusted by Faitharama</strong> · Vegan · Non-GMO · Gluten-free · Made with real fruit</span>
              </div>
            </div>

            {/* Right — real customer photo */}
            <div className="mx-auto md:mx-0" data-testid="goli-customer-photo">
              <div className="rounded-3xl overflow-hidden shadow-2xl transition-transform hover:scale-[1.02]"
                   style={{ border: `5px solid ${O}` }}>
                <img
                  src={GOLI_CUSTOMER}
                  alt="A real customer enjoying her Goli pre+post+probiotic gummy"
                  className="block w-full h-auto"
                  style={{ maxWidth: 420 }}
                  loading="eager"
                />
              </div>
              <div className="mt-3 text-center text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: G }}>
                ★ Real Goli Customer · Real Smile ★
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SLOGAN BANNER — Mike's rallying cry */}
      <section className="relative" data-testid="goli-faith-health-banner">
        <div className="max-w-6xl mx-auto px-6 md:px-8 pt-10 md:pt-14">
          <div className="rounded-3xl px-7 py-10 md:px-12 md:py-14 text-center shadow-lg"
               style={{
                 background: `linear-gradient(135deg, ${G} 0%, #1F8A2A 100%)`,
                 border: `4px solid ${O}`,
               }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-5"
                 style={{ background: O, color: "#0F172A" }}>
              <Heart className="w-3.5 h-3.5" fill="#0F172A" /> ★ MIKE&apos;S MESSAGE ★
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl uppercase leading-[1.05] mx-auto max-w-4xl"
                style={{ color: "#FFFFFF", textWrap: "balance" }}>
              It&apos;s Time For Christians To Stand Up For Their <span style={{ color: "#E0F2FE" }}>Faith And Wellness!</span>
            </h2>
            <p className="mt-6 text-base md:text-xl max-w-2xl mx-auto" style={{ color: "rgba(255,253,247,0.92)" }}>
              The world tells us to settle for tired bodies, processed food, and pills that don&apos;t work. <strong style={{ color: "#E0F2FE" }}>We were made for more.</strong> Real food. Real fruit. Real wellness — from companies that don&apos;t hide their ingredients.
            </p>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT — all the Goli sections in one flow */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <GoliGummiesCard />
      </section>

      {/* CALL-OUT FOOTER */}
      <section className="max-w-6xl mx-auto px-6 md:px-8 pb-20">
        <div className="rounded-2xl p-7 sm:p-9 text-center"
             style={{ background: "linear-gradient(135deg, #FFFFFF 0%, #FFFFFF 100%)", border: `2px solid ${O}` }}>
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ NEW TO <Brand />? ★
          </div>
          <h3 className="text-3xl md:text-4xl uppercase mt-2 leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, color: WINE }}>
            Start Your 7-Day Trial. Earn From Every Sale You Share.
          </h3>
          <p className="mt-2 max-w-2xl mx-auto" style={{ color: "#0F172A" }}>
            Goli is just one of many ways Faitharama members earn. Funnels, courses, your own store, affiliate commissions on Mike&apos;s book, merch, supplements — all from one $49/mo platform (start with a 7-day free trial).
          </p>
          <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/pricing"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-[0.18em] text-sm shadow-md transition text-white hover:scale-[1.03]"
                  style={{ background: G }} data-testid="goli-page-pricing-cta">
              See Pricing <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/signup"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg font-bold uppercase tracking-[0.18em] text-sm text-white transition hover:scale-[1.03]"
                  style={{ background: O }} data-testid="goli-page-signup-cta">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
